/**
 * The Design Value Index chart plays once when it comes on screen: the S&P 500 line draws, the
 * Design Value Index line climbs past it, each ball rides to its line's end and its label fades in,
 * the bracket draws between the two ends, and the counter rolls up to +228%. Every cue is the
 * composition's (101 frames at 25 fps), in milliseconds on the elements' data attributes.
 *
 * The two lines are strokes (modules/motion/stroke.js). The bracket, the balls, the labels, and
 * the counter digits move by translate, scale, and opacity on the compositor. The pending state
 * holds every part hidden until the piece plays (modules/motion/once.js); the resting markup is
 * the finished chart.
 */
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {rollDigit} from '../../modules/motion/digit.js';
import {cue, playOnce} from '../../modules/motion/once.js';
import {drawStroke} from '../../modules/motion/stroke.js';

const SELECTOR = Object.freeze({
    root: '.js-motionDesignValueIndex',
    line: '.js-motionDesignValueIndexLine',
    bracket: '.js-motionDesignValueIndexBracket',
    ball: '.js-motionDesignValueIndexBall',
    label: '.js-motionDesignValueIndexLabel',
    part: '.js-motionDesignValueIndexPart',
    column: '.js-motionDesignValueIndexColumn',
});

/** Milliseconds. The composition's cues for the strokes and the ball's pop; the rest ride on data attributes. */
const TIMING = Object.freeze({
    lines: [
        {delay: 200, duration: 2800},
        {delay: 440, duration: 2800},
    ],
    ballPop: 400,
});

const EASE = Object.freeze({
    /* The composition's draw and roll curve, to the nearest named one: a soft start into a long landing. */
    draw: EASING_CURVES['ease-step'],
    fade: EASING_CURVES['ease-out'],
    pop: EASING_CURVES['ease-out-back'],
});

/** Hold the first keyframe through the delay; the natural styles take over at the end. */
const FILL = 'backwards';

/**
 * The ball pops to size where its line has reached, then rides to the line's end through a waypoint.
 * @param {HTMLElement} ball
 * @returns {Animation[]}
 */
function ride(ball) {
    const {delay, duration} = cue(ball);
    const viaAt = (Number(ball.dataset.viaAt) - delay) / duration;

    return [
        ball.animate(
            [
                {opacity: 0, scale: 0},
                {opacity: 1, scale: 1},
            ],
            {delay, duration: TIMING.ballPop, easing: EASE.pop, fill: FILL}
        ),
        ball.animate([{translate: ball.dataset.from}, {translate: ball.dataset.via, offset: viaAt}, {translate: '0 0'}], {delay, duration, easing: EASE.draw, fill: FILL}),
    ];
}

/**
 * @param {Element} part
 * @returns {Animation}
 */
function fadeIn(part) {
    return part.animate([{opacity: 0}, {opacity: 1}], {...cue(part), easing: EASE.fade, fill: FILL});
}

/**
 * @param {HTMLElement} root
 * @param {import('../../modules/motion/run.js').Run} run
 */
function start(root, run) {
    [...root.querySelectorAll(SELECTOR.line)].forEach((line, index) => {
        if (line instanceof SVGElement) {
            run.add(drawStroke(line, {...(TIMING.lines[index] ?? TIMING.lines.at(-1)), easing: EASE.draw}));
        }
    });
    for (const ball of root.querySelectorAll(SELECTOR.ball)) {
        if (ball instanceof HTMLElement) {
            run.add(...ride(ball));
        }
    }
    for (const part of root.querySelectorAll(`${SELECTOR.label}, ${SELECTOR.part}, ${SELECTOR.bracket}`)) {
        if (part instanceof HTMLElement || part instanceof SVGElement) {
            run.add(fadeIn(part));
        }
    }
    for (const column of root.querySelectorAll(SELECTOR.column)) {
        if (column instanceof HTMLElement) {
            run.add(rollDigit(column, {...cue(column), easing: EASE.draw}));
        }
    }
}

/**
 * Initialize every chart that is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const root of document.querySelectorAll(SELECTOR.root)) {
        if (root instanceof HTMLElement) {
            playOnce(root, (run) => start(root, run));
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
